import React, { useState } from 'react';

const FIM_ITEMS = [
  { id: 'A', label: 'Eating',             category: 'Self-Care' },
  { id: 'B', label: 'Grooming',           category: 'Self-Care' },
  { id: 'C', label: 'Bathing',            category: 'Self-Care' },
  { id: 'D', label: 'Dressing – Upper',   category: 'Self-Care' },
  { id: 'E', label: 'Dressing – Lower',   category: 'Self-Care' },
  { id: 'F', label: 'Toileting',          category: 'Self-Care' },
  { id: 'G', label: 'Bladder Management', category: 'Sphincter Control' },
  { id: 'H', label: 'Bowel Management',   category: 'Sphincter Control' },
  { id: 'I', label: 'Bed/Chair/Wheelchair', category: 'Transfers' },
  { id: 'J', label: 'Toilet',             category: 'Transfers' },
  { id: 'K', label: 'Tub/Shower',         category: 'Transfers' },
  { id: 'L', label: 'Walk/Wheelchair',    category: 'Locomotion', modeOptions: ['W – Walk', 'C – Wheelchair', 'B – Both'] },
  { id: 'M', label: 'Stairs',             category: 'Locomotion' },
  { id: 'N', label: 'Comprehension',      category: 'Communication', modeOptions: ['A – Auditory', 'V – Visual', 'B – Both'] },
  { id: 'O', label: 'Expression',         category: 'Communication', modeOptions: ['V – Vocal', 'N – Nonvocal', 'B – Both'] },
  { id: 'P', label: 'Social Interaction', category: 'Social Cognition' },
  { id: 'Q', label: 'Problem Solving',    category: 'Social Cognition' },
  { id: 'R', label: 'Memory',             category: 'Social Cognition' },
];

const FIM_CATEGORIES = [
  { name: 'Self-Care',          color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  { name: 'Sphincter Control',  color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  { name: 'Transfers',          color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { name: 'Locomotion',         color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  { name: 'Communication',      color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { name: 'Social Cognition',   color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
];

const FIM_LEVELS = [
  { score: 7, short: '7', label: 'Complete Independence',    color: '#059669' },
  { score: 6, short: '6', label: 'Modified Independence',    color: '#10b981' },
  { score: 5, short: '5', label: 'Supervision / Setup',      color: '#84cc16' },
  { score: 4, short: '4', label: 'Minimal Assistance (75%)', color: '#d97706' },
  { score: 3, short: '3', label: 'Moderate Assistance (50%)',color: '#ea580c' },
  { score: 2, short: '2', label: 'Maximal Assistance (25%)', color: '#ef4444' },
  { score: 1, short: '1', label: 'Total Assistance (<25%)',  color: '#b91c1c' },
];

const getFIMColor = (score) => {
  if (!score) return '#94a3b8';
  const lvl = FIM_LEVELS.find(l => l.score === score);
  return lvl ? lvl.color : '#94a3b8';
};

const getFIMSeverity = (total, filled) => {
  if (filled === 0) return { label: 'Not assessed', color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0' };
  const max = filled * 7;
  const pct = total / max;
  if (pct >= 0.86) return { label: 'Functional Independence', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' };
  if (pct >= 0.57) return { label: 'Modified Dependence',     color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
  return               { label: 'Complete Dependence',        color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' };
};

const FIMAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const getScore = (id, phase) => value[`${id}_${phase}`] ?? null;
  const setScore = (id, phase, score) => {
    const key = `${id}_${phase}`;
    const current = value[key];
    onChange?.({ ...value, [key]: current === score ? null : score });
  };
  const getMode = (id) => value[`${id}_mode`] || null;
  const setMode = (id, mode) => onChange?.({ ...value, [`${id}_mode`]: mode });

  const phases = ['score'];
  const phaseLabels = { score: 'Score' };

  const scores = FIM_ITEMS.map(it => getScore(it.id, 'score')).filter(s => s !== null);
  const total = scores.reduce((a, b) => a + b, 0);
  const filledCount = scores.length;
  const severity = getFIMSeverity(total, filledCount);

  const ScoreBtn = ({ id, phase, score }) => {
    const selected = getScore(id, phase) === score;
    const c = getFIMColor(score);
    return (
      <button
        type="button"
        onClick={() => setScore(id, phase, score)}
        style={{
          width: '26px', height: '26px', borderRadius: '7px',
          border: `1.5px solid ${selected ? c : '#e2e8f0'}`,
          background: selected ? c : 'transparent',
          color: selected ? '#fff' : '#64748b',
          fontSize: '11px', fontWeight: 700, cursor: 'pointer',
          transition: 'all 0.15s'
        }}
      >{score}</button>
    );
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px',
          borderRadius: '14px', border: '1px solid #e2e8f0',
          backgroundColor: 'transparent', color: '#0e7490',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
          transition: 'all 0.2s'
        }}>
          + FIM Assessment (Functional Independence Measure)
          {filledCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#0e7490', color: '#fff', fontWeight: 800 }}>
              {filledCount}/18 scored
            </span>
          )}
          {filledCount > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: severity.color, color: '#fff', fontWeight: 800 }}>
              {total} / {filledCount * 7}
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px', position: 'relative' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#0e7490' }}>📊 FIM — Functional Independence Measure</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = '#ef4444'}
              onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '14px 18px', borderRadius: '12px',
            background: severity.bg, border: `1px solid ${severity.border}`,
            marginBottom: '16px'
          }}>
            <div style={{ textAlign: 'center', minWidth: '56px' }}>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: severity.color, lineHeight: 1 }}>{total}</div>
              <div style={{ fontSize: '11px', color: severity.color, fontWeight: 600 }}>/ {filledCount * 7}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ height: '7px', background: '#e5e7eb', borderRadius: '99px', marginBottom: '7px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '99px',
                  width: filledCount > 0 ? `${(total / (filledCount * 7)) * 100}%` : '0%',
                  background: severity.color, transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: severity.color }}>{severity.label}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                7 = Complete Independence · 4 = Min Assist · 1 = Total Assist
              </div>
            </div>
          </div>

          {/* Score level legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
            {FIM_LEVELS.map(lvl => (
              <div key={lvl.score} style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '3px 10px', borderRadius: '20px',
                background: '#fff', border: `1px solid ${lvl.color}30`,
                fontSize: '10px', color: lvl.color, fontWeight: 600
              }}>
                <span style={{ fontWeight: 800 }}>{lvl.score}</span> {lvl.label}
              </div>
            ))}
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #cffafe', background: '#fff', minWidth: '340px' }}>
            {/* Table Header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '28px 180px 1fr',
              background: '#f0fdff', borderBottom: '0.5px solid #cffafe',
              padding: '8px 14px', alignItems: 'center'
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0e7490' }}>#</div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0e7490' }}>Item</div>
              {phases.map(ph => (
                <div key={ph} style={{ fontSize: '11px', fontWeight: 700, color: '#0e7490', textAlign: 'center' }}>
                  {phaseLabels[ph]}
                </div>
              ))}
            </div>

            {FIM_CATEGORIES.map(cat => {
              const catItems = FIM_ITEMS.filter(it => it.category === cat.name);
              return (
                <div key={cat.name}>
                  {/* Category header row */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '28px 180px 1fr',
                    padding: '6px 14px',
                    background: cat.bg, borderBottom: '0.5px solid #f1f5f9',
                    borderTop: '0.5px solid #e2e8f0'
                  }}>
                    <div />
                    <div style={{ fontSize: '11px', fontWeight: 800, color: cat.color, gridColumn: '2 / 4', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {cat.name}
                    </div>
                  </div>

                  {/* Item rows */}
                  {catItems.map((item, idx) => {
                    const itemScore = getScore(item.id, 'score');
                    const anyFilled = getScore(item.id, 'score') !== null;
                    return (
                      <div key={item.id}>
                        <div style={{
                          display: 'grid', gridTemplateColumns: '28px 180px 1fr',
                          alignItems: 'center', padding: '9px 14px',
                          borderBottom: '0.5px solid #f1f5f9',
                          background: anyFilled ? '#fafeff' : '#fff',
                          transition: 'background 0.15s'
                        }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: cat.color }}>{item.id}</div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{item.label}</div>
                            {itemScore !== null && (
                              <div style={{ fontSize: '10px', color: getFIMColor(itemScore), fontWeight: 600, marginTop: '1px' }}>
                                {FIM_LEVELS.find(l => l.score === itemScore)?.label}
                              </div>
                            )}
                          </div>
                          {phases.map(ph => (
                            <div key={ph} style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'center' }}>
                              {[7, 6, 5, 4, 3, 2, 1].map(s => (
                                <ScoreBtn key={s} id={item.id} phase={ph} score={s} />
                              ))}
                            </div>
                          ))}
                        </div>

                        {/* Mode selector for special items */}
                        {item.modeOptions && (
                          <div style={{
                            display: 'grid', gridTemplateColumns: '28px 180px 1fr',
                            padding: '4px 14px 8px',
                            borderBottom: idx < catItems.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                            background: anyFilled ? '#fafeff' : '#fff'
                          }}>
                            <div />
                            <div style={{ gridColumn: '2 / 4', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>Mode:</span>
                              {item.modeOptions.map(opt => {
                                const active = getMode(item.id) === opt;
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => setMode(item.id, active ? null : opt)}
                                    style={{
                                      fontSize: '10px', fontWeight: 600,
                                      padding: '2px 9px', borderRadius: '20px', cursor: 'pointer',
                                      transition: 'all 0.15s',
                                      border: `1px solid ${active ? cat.color : '#e2e8f0'}`,
                                      background: active ? cat.bg : 'transparent',
                                      color: active ? cat.color : '#64748b'
                                    }}
                                  >{active ? '✓ ' : ''}{opt}</button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Totals row */}
            <div style={{
              display: 'grid', gridTemplateColumns: '28px 180px 1fr',
              padding: '10px 14px', background: '#f0fdff',
              borderTop: '1.5px solid #67e8f9', alignItems: 'center'
            }}>
              <div />
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#0e7490' }}>
                TOTAL
                <div style={{ fontSize: '10px', fontWeight: 500, color: '#94a3b8' }}>{filledCount} / 18 scored</div>
              </div>
              {phases.map(ph => {
                const phScores = FIM_ITEMS.map(it => getScore(it.id, ph)).filter(s => s !== null);
                const phTotal = phScores.reduce((a, b) => a + b, 0);
                const phMax = phScores.length * 7;
                return (
                  <div key={ph} style={{ textAlign: 'center', fontSize: '18px', fontWeight: 800, color: getFIMColor(phTotal > 0 ? Math.round(phTotal / phScores.length) : null) }}>
                    {phScores.length > 0 ? `${phTotal} / ${phMax}` : '—'}
                  </div>
                );
              })}
            </div>
          </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
            {Object.keys(value).length > 0 && (
              <button type="button" onClick={() => onChange({})} style={{ fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear all FIM data
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FIMAssessment;
